import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getRequests as getRequestList,
  createRequestApi,
  getRequest as getRequestApi,
  updateRequestApi,
  updateStatusRequestApi,
  deleteRequestApi,
  assignAgentsApi,
  submitQuoteApi,
  approveQuoteApi,
} from "../../helpers/";

// constants
import { RequestsActionTypes } from "./constants";
import {
  createRequestError,
  createRequestSuccess,
  requestsGetListError,
  requestsGetListSuccess,
  getRequestSuccess,
  getRequestError,
  updateRequestSuccess,
  updateRequestError,
  deleteRequestSuccess,
  deleteRequestError,
  updateStatusRequestSuccess,
  updateStatusRequestError,
  assignAgentsSuccess,
  assignAgentsError,
  submitQuoteSuccess,
  submitQuoteError,
  approveQuoteSuccess,
  approveQuoteError,
} from "./actions";

interface RequestData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    country?: string;
    status?: string;
    sourcingStatus?: string;
    startDate?: string;
    endDate?: string;
  };
  type: string;
}

interface RequestCreateData {
  payload: {
    country: string;
    comment: string;
    description: string;
    name: string;
    price: number;
    productImage: string;
    quantity: number;
  };
  type: string;
}

interface RequestUpdateData {
  payload: {
    id: string;
    comment: string;
    description: string;
    name: string;
    price: number;
    productImage: string;
    quantity: number;
  };
  type: string;
}

interface RequestDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetRequestData {
  payload: {
    id: string;
  };
  type: string;
}

function* getRequests({
  payload: { limit, skip, keyword, owner, country, status, sourcingStatus, startDate, endDate },
}: RequestData): SagaIterator {
  try {
    const params: any = { limit, skip, keyword, owner, country };
    if (status) params.status = status;
    if (sourcingStatus) params.sourcingStatus = sourcingStatus;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = yield call(getRequestList, params);
    const requests = response.data;
    yield put(
      requestsGetListSuccess(RequestsActionTypes.GET_LIST_REQUESTS, requests)
    );
  } catch (error: any) {
    yield put(
      requestsGetListError(RequestsActionTypes.GET_LIST_REQUESTS, error)
    );
  }
}

function* createRequest({
  payload: {
    country,
    comment,
    description,
    name,
    price,
    productImage,
    quantity,
  },
}: RequestCreateData): SagaIterator {
  try {
    const response = yield call(createRequestApi, {
      country,
      comment,
      description,
      name,
      price,
      productImage,
      quantity,
    });
    const request = response.data;
    yield put(
      createRequestSuccess(RequestsActionTypes.CREATE_REQUEST, request)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      createRequestError(RequestsActionTypes.CREATE_REQUEST, errorMessage)
    );
  }
}

function* updateRequest({
  payload: { id, comment, description, name, price, productImage, quantity },
}: RequestUpdateData): SagaIterator {
  try {
    const response = yield call(updateRequestApi, {
      id,
      comment,
      description,
      name,
      price,
      productImage,
      quantity,
    });
    const request = response.data;
    yield put(
      updateRequestSuccess(RequestsActionTypes.UPDATE_REQUEST, request)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateRequestError(RequestsActionTypes.UPDATE_REQUEST, errorMessage)
    );
  }
}

function* updateStatusRequest({
  payload: { id },
}: RequestUpdateData): SagaIterator {
  try {
    const response = yield call(updateStatusRequestApi, {
      id,
    });
    const request = response.data;
    yield put(
      updateStatusRequestSuccess(
        RequestsActionTypes.UPDATE_STATUS_REQUEST,
        request
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateStatusRequestError(
        RequestsActionTypes.UPDATE_STATUS_REQUEST,
        errorMessage
      )
    );
  }
}

function* deleteRequest({ payload: { ids } }: RequestDeleteData): SagaIterator {
  try {
    const response = yield call(deleteRequestApi, {
      ids,
    });
    const request = response.data;
    yield put(
      deleteRequestSuccess(RequestsActionTypes.DELETE_REQUEST, request)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      deleteRequestError(RequestsActionTypes.DELETE_REQUEST, errorMessage)
    );
  }
}

function* getRequest({ payload: { id } }: GetRequestData): SagaIterator {
  try {
    const response = yield call(getRequestApi, { id });
    const request = response.data;
    yield put(getRequestSuccess(RequestsActionTypes.GET_REQUEST, request));
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(getRequestError(RequestsActionTypes.GET_REQUEST, errorMessage));
  }
}

function* assignAgentsSaga({ payload: { id, agentIds } }: any): SagaIterator {
  try {
    const response = yield call(assignAgentsApi, { id, agentIds });
    yield put(assignAgentsSuccess(RequestsActionTypes.ASSIGN_AGENTS, response.data));
  } catch (error: any) {
    yield put(assignAgentsError(RequestsActionTypes.ASSIGN_AGENTS, error.response?.data || error.message));
  }
}

function* submitQuoteSaga({ payload }: any): SagaIterator {
  try {
    const { id, ...data } = payload;
    const response = yield call(submitQuoteApi, { id, ...data });
    yield put(submitQuoteSuccess(RequestsActionTypes.SUBMIT_QUOTE, response.data));
  } catch (error: any) {
    yield put(submitQuoteError(RequestsActionTypes.SUBMIT_QUOTE, error.response?.data || error.message));
  }
}

function* approveQuoteSaga({ payload: { id, approved, rejectionReason } }: any): SagaIterator {
  try {
    const response = yield call(approveQuoteApi, { id, approved, rejectionReason });
    yield put(approveQuoteSuccess(RequestsActionTypes.APPROVE_QUOTE, response.data));
  } catch (error: any) {
    yield put(approveQuoteError(RequestsActionTypes.APPROVE_QUOTE, error.response?.data || error.message));
  }
}

export function* watchGetRequests() {
  yield takeEvery(RequestsActionTypes.GET_LIST_REQUESTS, getRequests);
}

export function* watchCreateRequests() {
  yield takeEvery(RequestsActionTypes.CREATE_REQUEST, createRequest);
}

export function* watchUpdateRequests() {
  yield takeEvery(RequestsActionTypes.UPDATE_REQUEST, updateRequest);
}

export function* watchUpdateStatusRequests() {
  yield takeEvery(
    RequestsActionTypes.UPDATE_STATUS_REQUEST,
    updateStatusRequest
  );
}

export function* watchDeleteRequests() {
  yield takeEvery(RequestsActionTypes.DELETE_REQUEST, deleteRequest);
}

export function* watchGetRequest() {
  yield takeEvery(RequestsActionTypes.GET_REQUEST, getRequest);
}

export function* watchAssignAgents() {
  yield takeEvery(RequestsActionTypes.ASSIGN_AGENTS, assignAgentsSaga);
}
export function* watchSubmitQuote() {
  yield takeEvery(RequestsActionTypes.SUBMIT_QUOTE, submitQuoteSaga);
}
export function* watchApproveQuote() {
  yield takeEvery(RequestsActionTypes.APPROVE_QUOTE, approveQuoteSaga);
}

function* requestsSaga() {
  yield all([
    fork(watchGetRequests),
    fork(watchCreateRequests),
    fork(watchGetRequest),
    fork(watchUpdateRequests),
    fork(watchDeleteRequests),
    fork(watchUpdateStatusRequests),
    fork(watchAssignAgents),
    fork(watchSubmitQuote),
    fork(watchApproveQuote),
  ]);
}

export default requestsSaga;
